import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { IdCard, Loader2, Upload, CheckCircle2, Trash2 } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif';

const Face = ({ face, piece, busy, onFile, onRetirer }) => (
  <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`mo-piece-${face}`}>
    <span className="text-xs font-semibold capitalize text-slate-600">{face}</span>
    {piece ? (
      <span className="flex min-w-0 items-center gap-1.5 text-xs text-emerald-700" data-testid={`mo-piece-${face}-ok`}>
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Classée dans la GED</span>
        {onRetirer && (
          <button type="button" onClick={onRetirer} title="Retirer du devis" className="text-slate-400 hover:text-rose-600" data-testid={`mo-piece-${face}-retirer`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    ) : (
      <label className={`flex cursor-pointer items-center gap-1.5 rounded-md bg-[#0a3d62] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#0a3d62]/90 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Téléverser
        <input type="file" accept={ACCEPT} className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} data-testid={`mo-piece-${face}-input`} />
      </label>
    )}
  </div>
);

/** Pièce d'identité du maître d'ouvrage (facultative, recto/verso) — classée dans la GED du membre. */
export default function PieceIdentiteMO({ mo, membreId, onChange, sansRetrait = false }) {
  const [busy, setBusy] = useState('');
  const pieces = mo.pieces_identite || [];
  const de = (face) => pieces.find((p) => p.face === face);
  const envoyer = async (face, file) => {
    if (!file) return;
    if (!membreId) { toast.error('Sélectionnez d\'abord le membre'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Fichier supérieur à 10 Mo'); return; }
    setBusy(face);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('face', face); fd.append('membre_id', membreId);
      fd.append('maitre_ouvrage', JSON.stringify({ ...mo, pieces_identite: undefined }));
      const { data } = await axios.post(`${API}/registre-maitres-ouvrage/piece-identite`, fd, { headers: { Authorization: `Bearer ${getToken()}` } });
      onChange({ ...mo, pieces_identite: [...pieces.filter((p) => p.face !== face), data] });
      toast.success(`Pièce d'identité (${face}) classée dans la GED`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Téléversement impossible');
    } finally { setBusy(''); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-3" data-testid="mo-piece-identite">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
        <IdCard className="h-4 w-4 text-[#0a3d62]" /> Pièce d'identité du maître d'ouvrage <span className="font-normal text-slate-400">(facultative — PDF ou photo, 10 Mo max)</span>
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {['recto', 'verso'].map((f) => (
          <Face key={f} face={f} piece={de(f)} busy={busy === f} onFile={(file) => envoyer(f, file)}
            onRetirer={sansRetrait ? null : () => onChange({ ...mo, pieces_identite: pieces.filter((p) => p.face !== f) })} />
        ))}
      </div>
    </div>
  );
}
